import React, { useState } from "react"
import { Box, FormControl, FormHelperText, FormLabel, IconButton, Skeleton, Typography } from "@mui/material"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import Collapse from "@mui/material/Collapse"
import { getCurrencyStringFromNumber } from "../../../Util/Util"
import OptionTileSelector from "../OptionTileSelector"

// Stringer type tiles with wood color swatches
const StringerTile = ({ opt, selected, disabled, onSelect }) => {
  const color = opt.extra_data?.hex_color || "#999"
  let label = opt.value
  if (opt.price > 0) label += ` ${getCurrencyStringFromNumber(opt.price, true)}`
  return (
    <Box
      onClick={() => { if (!disabled) onSelect(opt) }}
      sx={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
        px: 1.5, py: 1, borderRadius: 1.5, cursor: disabled ? "default" : "pointer",
        border: selected ? "1.5px solid #c8b99a" : "1.5px solid transparent",
        bgcolor: selected ? "rgba(200,185,154,0.1)" : "rgba(255,255,255,0.04)",
        transition: "all 0.15s", flex: "1 1 0", minWidth: 62, maxWidth: 100,
        "&:hover": disabled ? {} : { border: "1.5px solid rgba(255,255,255,0.2)" },
      }}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: color, border: "2px solid rgba(255,255,255,0.15)" }} />
      <Typography sx={{
        fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.55rem",
        textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center",
        lineHeight: 1.2, color: selected ? "#c8b99a" : "rgba(255,255,255,0.45)",
      }}>
        {label}
      </Typography>
    </Box>
  )
}

const StringerTypeSelector = ({ title, option, options, updateOptions, orderData, isStaff }) => {
  if (!option) return <Skeleton />
  const disabled = orderData?.locked && !isStaff
  return (
    <Box my={1}>
      <FormControl fullWidth>
        <FormLabel><Typography variant="caption">{title}</Typography></FormLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mt: 0.5 }}>
          {options.map((opt) => (
            <StringerTile key={opt.value} opt={opt} selected={option?.value === opt.value}
              disabled={disabled} onSelect={(o) => updateOptions([o])} />
          ))}
        </Box>
        {option?.help_text && <FormHelperText sx={{ textAlign: "center" }}>{option.help_text}</FormHelperText>}
      </FormControl>
    </Box>
  )
}

// SVG maps for number of stringers
const StringerCountSvgs = {
  None: () => (
    <svg viewBox="0 0 30 60" width="24" height="50">
      <line x1="5" y1="30" x2="25" y2="30" stroke="currentColor" strokeWidth={1.2} strokeDasharray="3 3" opacity={0.4} />
    </svg>
  ),
  Single: () => (
    <svg viewBox="0 0 30 60" width="24" height="50">
      <line x1="15" y1="4" x2="15" y2="56" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  ),
  Double: () => (
    <svg viewBox="0 0 30 60" width="24" height="50">
      <line x1="11" y1="4" x2="11" y2="56" stroke="currentColor" strokeWidth={1.5} />
      <line x1="19" y1="4" x2="19" y2="56" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  ),
  Triple: () => (
    <svg viewBox="0 0 30 60" width="24" height="50">
      <line x1="8" y1="4" x2="8" y2="56" stroke="currentColor" strokeWidth={1.5} />
      <line x1="15" y1="4" x2="15" y2="56" stroke="currentColor" strokeWidth={1.5} />
      <line x1="22" y1="4" x2="22" y2="56" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  ),
  Five: () => (
    <svg viewBox="0 0 30 60" width="24" height="50">
      <line x1="5" y1="4" x2="5" y2="56" stroke="currentColor" strokeWidth={1.2} />
      <line x1="10" y1="4" x2="10" y2="56" stroke="currentColor" strokeWidth={1.2} />
      <line x1="15" y1="4" x2="15" y2="56" stroke="currentColor" strokeWidth={1.2} />
      <line x1="20" y1="4" x2="20" y2="56" stroke="currentColor" strokeWidth={1.2} />
      <line x1="25" y1="4" x2="25" y2="56" stroke="currentColor" strokeWidth={1.2} />
    </svg>
  ),
}

// Foam density — Normal=balance, Lightweight=feather, Heavy=anchor
const FoamSvgs = {
  Normal: () => (
    <svg viewBox="0 0 40 44" width="30" height="36">
      <path d="M16 40 L20 32 L24 40 Z" stroke="currentColor" fill="none" strokeWidth={1.2} strokeLinejoin="round" />
      <line x1="6" y1="18" x2="34" y2="18" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" />
      <line x1="20" y1="18" x2="20" y2="32" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" />
      <path d="M4 18 Q4 24, 10 24 L6 24 Q12 24, 12 18" stroke="currentColor" fill="none" strokeWidth={1.1} />
      <path d="M28 18 Q28 24, 34 24 L30 24 Q36 24, 36 18" stroke="currentColor" fill="none" strokeWidth={1.1} />
    </svg>
  ),
  Lightweight: () => (
    <svg viewBox="0 0 36 44" width="28" height="36">
      <path d="M18 4 Q26 12, 26 22 Q26 32, 18 40" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" />
      <path d="M18 4 Q10 12, 10 22 Q10 32, 18 40" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" />
      <line x1="18" y1="4" x2="18" y2="40" stroke="currentColor" strokeWidth={1} strokeLinecap="round" />
      <line x1="18" y1="14" x2="12" y2="16" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
      <line x1="18" y1="14" x2="24" y2="16" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
      <line x1="18" y1="22" x2="11" y2="24" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
      <line x1="18" y1="22" x2="25" y2="24" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
      <line x1="18" y1="30" x2="13" y2="32" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
      <line x1="18" y1="30" x2="23" y2="32" stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" />
    </svg>
  ),
  Heavy: () => (
    <svg viewBox="0 0 36 44" width="28" height="36">
      <circle cx="18" cy="8" r="4" stroke="currentColor" fill="none" strokeWidth={1.3} />
      <line x1="18" y1="12" x2="18" y2="36" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="10" y1="20" x2="26" y2="20" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" />
      <path d="M8 38 Q10 30, 18 36" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" />
      <path d="M28 38 Q26 30, 18 36" stroke="currentColor" fill="none" strokeWidth={1.3} strokeLinecap="round" />
    </svg>
  ),
}

const Blank = ({
  updateOptions,
  selectedStringerTypeOption,
  setSelectedStringerTypeOption,
  stringerTypeOptions,
  selectedNumberOfStringersOption,
  setSelectedNumberOfStringersOption,
  numberOfStringersOptions,
  selectedFoamDensityOption,
  setSelectedFoamDensityOption,
  foamDensityOptions,
  orderData,
  isStaff,
}) => {
  const [open, setOpen] = useState(true)

  return (
    <React.Fragment>
      <Box pt={3} display="flex" alignItems="center" justifyContent="center" sx={{ position: "relative" }}>
        <Typography variant="h5">Blank</Typography>
        <IconButton onClick={() => setOpen(!open)} sx={{ position: "absolute", right: 0 }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <StringerTypeSelector
          isStaff={isStaff}
          orderData={orderData}
          updateOptions={updateOptions}
          title={"Stringer Type"}
          option={selectedStringerTypeOption}
          options={stringerTypeOptions}
        />
        <OptionTileSelector
          isStaff={isStaff}
          orderData={orderData}
          updateOptions={updateOptions}
          title={"Number of Stringers"}
          option={selectedNumberOfStringersOption}
          options={numberOfStringersOptions}
          svgMap={StringerCountSvgs}
        />
        <OptionTileSelector
          isStaff={isStaff}
          orderData={orderData}
          updateOptions={updateOptions}
          title={"Foam Density"}
          option={selectedFoamDensityOption}
          options={[...foamDensityOptions].sort((a, b) => {
            const order = { "Lightweight": 0, "Normal": 1, "Heavy": 2 }
            return (order[a.value] ?? 9) - (order[b.value] ?? 9)
          })}
          svgMap={FoamSvgs}
        />
      </Collapse>
    </React.Fragment>
  )
}

export default Blank
